import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import {
  listTipReminderStages,
  updateTipReminderStage,
  listTipReminderLog,
  resendTipReminder,
  lookupShiftForTipReminder,
} from "@/lib/tip-reminders-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/tip-reminders")({
  head: () => ({ meta: [{ title: "Tip reminders — Admin" }] }),
  component: AdminTipRemindersPage,
});

type Stage = {
  id: string;
  enabled: boolean;
  min_hours: number;
  max_hours: number;
  title: string;
  body_template: string;
  email_badge: string | null;
  sort_order: number;
  trigger_type: "hour_window" | "noon_next_day";
};

type LogRow = {
  id: string;
  shift_id: string;
  venue_id: string;
  stage_id: string;
  status: "sent" | "skipped" | "failed";
  skip_reason: string | null;
  error_message: string | null;
  triggered_by: "cron" | "admin_resend";
  triggered_by_user_id: string | null;
  created_at: string;
};

function fmt(iso: string) { return new Date(iso).toLocaleString("en-GB"); }

function AdminTipRemindersPage() {
  const fetchStages = useServerFn(listTipReminderStages);
  const saveStage = useServerFn(updateTipReminderStage);
  const fetchLog = useServerFn(listTipReminderLog);
  const lookupShift = useServerFn(lookupShiftForTipReminder);
  const resend = useServerFn(resendTipReminder);

  const [stages, setStages] = useState<Stage[]>([]);
  const [log, setLog] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Partial<Stage>>>({});

  const [resendShiftId, setResendShiftId] = useState("");
  const [resendStageId, setResendStageId] = useState("noon_next_day");
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);

  const reload = async () => {
    const [s, l] = await Promise.all([fetchStages(), fetchLog({ data: { limit: 100 } })]);
    setStages(s.stages as Stage[]);
    setLog(l.rows as LogRow[]);
    setLoading(false);
  };

  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const draftOf = (s: Stage) => ({ ...s, ...drafts[s.id] });
  const setDraft = (id: string, patch: Partial<Stage>) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const onSave = async (s: Stage) => {
    setSavingId(s.id);
    const d = draftOf(s);
    try {
      await saveStage({
        data: {
          id: s.id,
          enabled: d.enabled,
          title: d.title,
          bodyTemplate: d.body_template,
          emailBadge: d.email_badge ?? null,
          ...(d.trigger_type === "hour_window"
            ? { minHours: Number(d.min_hours), maxHours: Number(d.max_hours) }
            : {}),
        },
      });
      setDrafts((all) => { const { [s.id]: _, ...rest } = all; return rest; });
      setSavedFlash(s.id);
      window.setTimeout(() => setSavedFlash(null), 1500);
      await reload();
    } finally {
      setSavingId(null);
    }
  };

  const onResend = async () => {
    setResendBusy(true);
    setResendStatus(null);
    try {
      const { shift } = await lookupShift({ data: { shiftId: resendShiftId.trim() } });
      if (!shift) { setResendStatus("Shift not found."); return; }
      const r = await resend({ data: { shiftId: resendShiftId.trim(), stageId: resendStageId } });
      setResendStatus(
        r.status === "sent" ? "Sent ✔"
        : r.status === "skipped" ? `Skipped: ${r.reason ?? "unknown"}`
        : `Failed: ${r.error ?? "unknown"}`
      );
      await reload();
    } catch (e) {
      setResendStatus(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Admin</p>
        <h1 className="mt-3 font-display text-3xl">Tip-allocation reminders</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Edit subject &amp; body copy for each venue tip-allocation reminder, replay the
          12pm next-day reminder for a specific shift, and inspect every delivery attempt
          for debugging. Body templates support <code>{"{LOC}"}</code> and <code>{"{LOC_PREFIX}"}</code>.
        </p>

        {/* STAGES */}
        <h2 className="mt-10 font-display text-2xl">Stages</h2>
        {loading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <ul className="mt-4 space-y-4">
            {stages.map((s) => {
              const d = draftOf(s);
              const dirty = drafts[s.id] != null;
              return (
                <li key={s.id} className="border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-display text-lg">{s.id}</p>
                      <p className="text-[11px] tracking-wide-luxury text-muted-foreground">
                        {s.trigger_type === "noon_next_day"
                          ? "Fires at UK 12:00 the day after the shift"
                          : `Fires ${s.min_hours}h–${s.max_hours}h after shift end`}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!d.enabled}
                        onChange={(e) => setDraft(s.id, { enabled: e.target.checked })}
                      />
                      Enabled
                    </label>
                  </div>

                  <label className="mt-3 block text-xs tracking-wide-luxury text-muted-foreground">Email subject / heading</label>
                  <input
                    className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm"
                    value={d.title ?? ""}
                    onChange={(e) => setDraft(s.id, { title: e.target.value })}
                  />

                  <label className="mt-3 block text-xs tracking-wide-luxury text-muted-foreground">Body</label>
                  <textarea
                    className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm"
                    rows={3}
                    value={d.body_template ?? ""}
                    onChange={(e) => setDraft(s.id, { body_template: e.target.value })}
                  />

                  <div className="mt-3 flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[160px]">
                      <label className="block text-xs tracking-wide-luxury text-muted-foreground">Email badge</label>
                      <input
                        className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm"
                        value={d.email_badge ?? ""}
                        onChange={(e) => setDraft(s.id, { email_badge: e.target.value })}
                      />
                    </div>
                    {s.trigger_type === "hour_window" && (
                      <>
                        <div className="w-28">
                          <label className="block text-xs tracking-wide-luxury text-muted-foreground">Min hours</label>
                          <input type="number" className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm"
                            value={d.min_hours ?? 0}
                            onChange={(e) => setDraft(s.id, { min_hours: Number(e.target.value) })}
                          />
                        </div>
                        <div className="w-28">
                          <label className="block text-xs tracking-wide-luxury text-muted-foreground">Max hours</label>
                          <input type="number" className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm"
                            value={d.max_hours ?? 0}
                            onChange={(e) => setDraft(s.id, { max_hours: Number(e.target.value) })}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <button
                      onClick={() => onSave(s)}
                      disabled={!dirty || savingId === s.id}
                      className="border border-accent px-4 py-2 text-xs tracking-wide-luxury hover:bg-accent/10 disabled:opacity-50"
                    >
                      {savingId === s.id ? "Saving…" : "Save changes"}
                    </button>
                    {savedFlash === s.id && <span className="text-xs text-emerald-500">Saved</span>}
                    {dirty && savedFlash !== s.id && (
                      <button
                        onClick={() => setDrafts((all) => { const { [s.id]: _, ...rest } = all; return rest; })}
                        className="text-xs underline text-muted-foreground"
                      >Reset</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* RESEND */}
        <h2 className="mt-12 font-display text-2xl">Resend reminder for a shift</h2>
        <div className="mt-4 border border-border bg-card p-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_220px_auto]">
            <input
              placeholder="Shift ID (UUID)"
              value={resendShiftId}
              onChange={(e) => setResendShiftId(e.target.value)}
              className="border border-border bg-background px-3 py-2 text-sm"
            />
            <select
              value={resendStageId}
              onChange={(e) => setResendStageId(e.target.value)}
              className="border border-border bg-background px-3 py-2 text-sm"
            >
              {stages.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
            </select>
            <button
              onClick={onResend}
              disabled={!resendShiftId.trim() || resendBusy}
              className="border border-accent px-4 py-2 text-xs tracking-wide-luxury hover:bg-accent/10 disabled:opacity-50"
            >
              {resendBusy ? "Sending…" : "Resend now"}
            </button>
          </div>
          {resendStatus && (
            <p className="mt-3 text-xs text-muted-foreground">{resendStatus}</p>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">
            Bypasses the "already dispatched" check. Skipped if tips are already marked
            paid / not applicable for that shift.
          </p>
        </div>

        {/* AUDIT LOG */}
        <h2 className="mt-12 font-display text-2xl">Recent send log</h2>
        {log.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No reminder attempts yet.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {log.map((r) => {
              const tone =
                r.status === "sent" ? "border-emerald-400/40"
                : r.status === "failed" ? "border-destructive/60"
                : "border-amber-400/40";
              return (
                <li key={r.id} className={`border ${tone} bg-card p-3 text-xs`}>
                  <p className="font-mono">
                    {fmt(r.created_at)} · {r.stage_id} · {r.status.toUpperCase()}
                    {" · "}via {r.triggered_by}
                  </p>
                  <p className="mt-1 text-muted-foreground break-all">
                    shift {r.shift_id}
                  </p>
                  {r.skip_reason && <p className="mt-1 text-amber-500">skip: {r.skip_reason}</p>}
                  {r.error_message && <p className="mt-1 text-destructive break-all">error: {r.error_message}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
